/**
 * SQL Notebook cell and content types.
 *
 * Uses discriminated unions for code vs markdown cells.
 * Cell state is split into persisted (source, order, connection) and
 * ephemeral (outputs, execution state) to match the persistence contract.
 */

import type { QueryResultSet, QueryMessage } from '../../core/types/workspaceTypes';
import type { ChartConfig } from '../../charts/types/chartTypes';

// ============================================================================
// Cell Execution Types
// ============================================================================

/** Execution state for a code cell */
export type CellExecutionState = 'idle' | 'executing' | 'cancelled' | 'completed' | 'error';

/** Save mode for .ipynb export */
export type NotebookSaveMode = 'rich' | 'clean';

// ============================================================================
// Cell Output Types
// ============================================================================

/** Output from a code cell execution */
export type CellOutput = {
  resultSets: QueryResultSet[];
  messages: QueryMessage[];
  /** Execution duration in milliseconds */
  durationMs?: number;
  /** Total row count (before display cap) */
  totalRowCount?: number;
};

// ============================================================================
// Cell Types (discriminated union)
// ============================================================================

/** Base fields shared by all cell types */
type CellBase = {
  /** Stable UUID for MCP tooling and diff friendliness */
  id: string;
  /** Raw source content */
  source: string;
};

/** SQL code cell */
export type NotebookCodeCell = CellBase & {
  cellType: 'code';
  /** Ephemeral: cell output (not persisted in session restore) */
  outputs?: CellOutput;
  /** Ephemeral: current execution state */
  executionState: CellExecutionState;
  /** Ephemeral: execution order number (e.g. [1], [2]) */
  executionOrder?: number;
  /** Ephemeral: outputs are from a previous connection (dimmed + tooltip) */
  isOutputStale?: boolean;
  /** Persisted: chart visualization config for cell results */
  chartConfig?: ChartConfig;
};

/** JavaScript code cell — executed in sandboxed Node.js context */
export type NotebookJavaScriptCell = CellBase & {
  cellType: 'javascript';
  /** Ephemeral: cell output (not persisted in session restore) */
  outputs?: CellOutput;
  /** Ephemeral: current execution state */
  executionState: CellExecutionState;
  /** Ephemeral: execution order number (e.g. [1], [2]) */
  executionOrder?: number;
  /** Ephemeral: outputs are from a previous connection (dimmed + tooltip) */
  isOutputStale?: boolean;
  /** Persisted: chart visualization config for cell results */
  chartConfig?: ChartConfig;
};

/** Markdown documentation cell */
export type NotebookMarkdownCell = CellBase & {
  cellType: 'markdown';
  /** Whether the cell is currently in edit mode */
  isEditing: boolean;
};

/** Discriminated union of all cell types */
export type NotebookCell = NotebookCodeCell | NotebookJavaScriptCell | NotebookMarkdownCell;

/** Cell types that can be executed (have outputs, execution state) */
export type ExecutableCell = NotebookCodeCell | NotebookJavaScriptCell;

// ============================================================================
// Cell Type Guards
// ============================================================================

export function isCodeCell(cell: NotebookCell): cell is NotebookCodeCell {
  return cell.cellType === 'code';
}

export function isJavaScriptCell(cell: NotebookCell): cell is NotebookJavaScriptCell {
  return cell.cellType === 'javascript';
}

export function isExecutableCell(cell: NotebookCell): cell is ExecutableCell {
  return cell.cellType === 'code' || cell.cellType === 'javascript';
}

export function isMarkdownCell(cell: NotebookCell): cell is NotebookMarkdownCell {
  return cell.cellType === 'markdown';
}

// ============================================================================
// Notebook Metadata
// ============================================================================

/** Jam SQL-specific metadata stored in .ipynb */
export type NotebookJamSqlMetadata = {
  version: 1;
  saveMode?: NotebookSaveMode;
  connectionName?: string;
  engine?: string;
};

// ============================================================================
// Notebook Tab Content
// ============================================================================

/**
 * Content for notebook workspace tabs.
 *
 * Persisted in session: cells (source + order), connection info, filePath, metadata.
 * NOT persisted: cell outputs, execution state (reset to idle on restore).
 */
export type NotebookTabContent = {
  /** Ordered array of cells */
  cells: NotebookCell[];
  /** File path for save-back (undefined for new unsaved notebooks) */
  filePath?: string;
  /** Hash of the last saved content for dirty tracking */
  lastSavedHash?: string;
  /** Jam SQL metadata */
  metadata: NotebookJamSqlMetadata;
  /** Global execution order counter (increments per execution) */
  executionCounter: number;
  /** Whether the notebook has unsaved changes */
  isDirty: boolean;
  /** AI Chat session ID for resuming conversations (persisted) */
  aiChatSessionId?: string;
  /** AI Chat sidebar open state (persisted) */
  aiChatSidebarOpen?: boolean;
};

// ============================================================================
// Cell Factory
// ============================================================================

/** Create a new empty code cell */
export function createCodeCell(id: string, source = ''): NotebookCodeCell {
  return {
    id,
    cellType: 'code',
    source,
    executionState: 'idle',
  };
}

/** Create a new empty JavaScript cell */
export function createJavaScriptCell(id: string, source = ''): NotebookJavaScriptCell {
  return {
    id,
    cellType: 'javascript',
    source,
    executionState: 'idle',
  };
}

/** Create a new empty markdown cell */
export function createMarkdownCell(id: string, source = '', isEditing = true): NotebookMarkdownCell {
  return {
    id,
    cellType: 'markdown',
    source,
    isEditing,
  };
}

/** All cell types that can be created */
export type NotebookCellType = 'code' | 'javascript' | 'markdown';
